import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { FilterMatchMode } from 'primereact/api';
import moment from 'moment';
import { getServerMastersList, saveServerMaster } from '../../utilities/api';
import '../CategoryMaster.css';

const statusOptions = [
    { label: 'Active', value: 'Active' },
    { label: 'Inactive', value: 'Inactive' }
];

const tables = [
    { label: 'Sections', value: 'sections', pk: 'sectionId', icon: 'pi pi-th-large' },
    { label: 'Roles', value: 'roles', pk: 'roleId', icon: 'pi pi-users' },
    { label: 'UOM', value: 'uom_creation', pk: 'uomId', icon: 'pi pi-box' },
    { label: 'Tax', value: 'tax_creation', pk: 'taxId', icon: 'pi pi-percentage' },
    { label: 'Platforms', value: 'platform', pk: 'platform_id', icon: 'pi pi-desktop' },
    { label: 'Master', value: 'master', pk: 'master_id', icon: 'pi pi-database' },
    { label: 'Message Type', value: 'message_type', pk: 'message_type_id', icon: 'pi pi-envelope' },
    { label: 'Setting', value: 'setting', pk: 'setting_id', icon: 'pi pi-cog' },
    { label: 'Supplier', value: 'supplier', pk: 'supplier_id', icon: 'pi pi-truck' }
];

const VALIDATION_RULES = {
    sections: {
        section_name: { required: true, maxLength: 50, label: 'Section Name' },
        section_remarks: { required: false, maxLength: 255, label: 'Section Remarks' },
        section_status: { required: true, label: 'Status' }
    },
    roles: {
        role: { required: true, maxLength: 255, label: 'Role' },
        role_status: { required: true, label: 'Status' }
    },
    uom_creation: {
        uom_name: { required: true, maxLength: 255, label: 'UOM Name' },
        uom_status: { required: true, label: 'Status' }
    },
    tax_creation: {
        tax_name: { required: true, maxLength: 255, label: 'Tax Name' },
        tax_percentage: { required: true, type: 'number', label: 'Tax Percentage' },
        tax_status: { required: true, label: 'Status' }
    },
    platform: {
        platform_name: { required: true, maxLength: 255, label: 'Platform Name' },
        platform_status: { required: true, label: 'Status' }
    },
    master: {
        master_name: { required: true, maxLength: 255, label: 'Master Name' },
        master_status: { required: true, label: 'Status' }
    },
    message_type: {
        message_type_name: { required: true, maxLength: 255, label: 'Message Type Name' },
        message_type_status: { required: true, label: 'Status' }
    },
    setting: {
        setting_name: { required: true, maxLength: 255, label: 'Setting Name' },
        setting_status: { required: true, label: 'Status' }
    },
    supplier: {
        supplier_name: { required: true, maxLength: 255, label: 'Supplier Name' },
        supplier_mob: { required: false, maxLength: 255, type: 'mobile', label: 'Mobile Number' },
        supplier_address: { required: false, maxLength: 255, label: 'Address' },
        supplier_joining_date: { required: false, type: 'date', label: 'Joining Date' },
        supplier_status: { required: true, label: 'Status' }
    }
};

const UNIQUE_FIELDS = {
    sections: ['section_name'],
    roles: ['role'],
    uom_creation: ['uom_name'],
    tax_creation: ['tax_name'],
    platform: ['platform_name'],
    master: ['master_name'],
    message_type: ['message_type_name'],
    setting: ['setting_name'],
    supplier: ['supplier_name']
};

const STATUS_FIELD = {
    sections: 'section_status',
    roles: 'role_status',
    uom_creation: 'uom_status',
    tax_creation: 'tax_status',
    platform: 'platform_status',
    master: 'master_status',
    message_type: 'message_type_status',
    setting: 'setting_status',
    supplier: 'supplier_status'
};

const Parentmasters = () => {
    const [active, setActive] = useState('roles');
    const [data, setData] = useState([]);
    const [platforms, setPlatforms] = useState([]);
    const [item, setItem] = useState({});
    const [errors, setErrors] = useState({});
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState(null);
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS }
    });
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const toast = useRef(null);

    const cur = tables.find((t) => t.value === active) || tables[1];

    const statusField = STATUS_FIELD[active];
    const hasStatusField = data.length > 0 && statusField && data[0][statusField] !== undefined;
    const filteredData = statusFilter && hasStatusField ? data.filter((row) => row[statusField] === statusFilter) : data;
    const activeCount = hasStatusField ? data.filter((r) => r[statusField] === 'Active').length : 0;
    const inactiveCount = hasStatusField ? data.filter((r) => r[statusField] === 'Inactive').length : 0;

    const visibleKeys = data.length > 0 ? Object.keys(data[0]).filter((k) => !['created', 'updated'].includes(k.toLowerCase())) : [];
    const formKeys = data.length > 0 ? Object.keys(data[0]).filter((k) => !['created', 'updated', cur.pk].includes(k)) : [];

    useEffect(() => {
        setData([]);
        setGlobalFilterValue('');
        setStatusFilter(null);
        setItem({});
        setErrors({});
        setFilters({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
        loadData(active);
    }, [active]);

    useEffect(() => {
        loadPlatforms();
    }, []);

    const loadData = async (t) => {
        setLoading(true);
        try {
            const j = await getServerMastersList(t);
            setData(Array.isArray(j) ? j : []);
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: `Could not load ${t}` });
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    const loadPlatforms = async () => {
        try {
            const j = await getServerMastersList('platform');
            if (Array.isArray(j)) setPlatforms(j.map((p) => ({ label: p.platform_name, value: p.platform_id })));
        } catch (error) {
            console.error('Error loading platforms:', error);
        }
    };

    const validateForm = () => {
        const validationRules = VALIDATION_RULES[active] || {};
        const newErrors = {};

        Object.keys(validationRules).forEach((field) => {
            if (!formKeys.includes(field)) return;
            const rule = validationRules[field];
            const value = item[field];

            if (rule.required && (!value || (typeof value === 'string' && !value.trim()))) {
                newErrors[field] = `${rule.label} is required`;
                return;
            }
            if (!value || (typeof value === 'string' && !value.trim())) return;
            if (rule.maxLength && typeof value === 'string' && value.length > rule.maxLength) {
                newErrors[field] = `${rule.label} must not exceed ${rule.maxLength} characters`;
            }
            if (rule.type === 'number' && isNaN(parseFloat(value))) {
                newErrors[field] = `${rule.label} must be a valid number`;
            }
            if (rule.type === 'mobile' && value && value !== '0') {
                const mobileRegex = /^[0-9]{10,15}$/;
                if (!mobileRegex.test(value.replace(/[\s\-\+]/g, ''))) {
                    newErrors[field] = `${rule.label} must be a valid phone number`;
                }
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const checkDuplicates = () => {
        const uniqueFields = UNIQUE_FIELDS[active] || [];
        for (const field of uniqueFields) {
            const value = item[field];
            if (!value || (typeof value === 'string' && !value.trim())) continue;
            const isDuplicate = data.some((row) => {
                if (item[cur.pk] && String(row[cur.pk]) === String(item[cur.pk])) return false;
                return String(row[field]).toLowerCase().trim() === String(value).toLowerCase().trim();
            });
            if (isDuplicate) {
                const fieldLabel = VALIDATION_RULES[active]?.[field]?.label || field;
                toast.current?.show({ severity: 'warn', summary: 'Duplicate Record', detail: `${fieldLabel} "${value}" already exists`, life: 5000 });
                return false;
            }
        }
        return true;
    };

    const confirmSave = () => {
        if (!validateForm()) {
            toast.current?.show({ severity: 'error', summary: 'Validation Error', detail: 'Please fix the highlighted fields' });
            return;
        }
        if (!checkDuplicates()) return;

        confirmDialog({
            message: item[cur.pk] ? `Are you sure you want to update this ${cur.label}?` : `Are you sure you want to add this new ${cur.label}?`,
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            accept: () => saveItem(),
            reject: () => toast.current?.show({ severity: 'info', summary: 'Cancelled', detail: 'Operation cancelled' }),
            acceptLabel: 'Yes, Proceed',
            rejectLabel: 'Cancel',
            acceptClassName: 'p-button-success',
            rejectClassName: 'p-button-text'
        });
    };

    const saveItem = async () => {
        try {
            const payload = { ...item };
            if (payload.supplier_joining_date instanceof Date) {
                payload.supplier_joining_date = moment(payload.supplier_joining_date).format('YYYY-MM-DD');
            }
            const j = await saveServerMaster(active, payload);
            if (j.status === 'success') {
                toast.current?.show({ severity: 'success', summary: 'Saved', detail: 'Record saved successfully' });
                setVisible(false);
                setItem({});
                setErrors({});
                loadData(active);
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: j.message || 'Save failed' });
            }
        } catch (error) {
            console.error('Save error:', error);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Save failed' });
        }
    };

    const handleResetFilters = () => {
        setGlobalFilterValue('');
        setStatusFilter(null);
        setFilters({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
    };

    const renderCell = (key, row) => {
        const lk = key.toLowerCase();
        if (lk.includes('platform') && active !== 'platform') {
            const p = platforms.find((p) => String(p.value) === String(row[key]));
            return p ? p.label : row[key];
        }
        if (lk.includes('status')) {
            const isActive = row[key] === 'Active';
            return <Tag value={row[key]} severity={isActive ? 'success' : 'danger'} rounded style={{ minWidth: '70px', textAlign: 'center' }} />;
        }
        if (lk.includes('date') && row[key]) {
            return moment(row[key]).isValid() ? moment(row[key]).format('DD-MM-YYYY') : row[key];
        }
        return row[key];
    };

    const actionBodyTemplate = (rd) => (
        <Button
            icon="pi pi-pencil"
            rounded
            text
            className="p-button-warning"
            onClick={() => {
                const editData = { ...rd };
                if (editData.supplier_joining_date) {
                    editData.supplier_joining_date = moment(editData.supplier_joining_date, ['YYYY-MM-DD', 'DD-MM-YYYY']).toDate();
                }
                setItem(editData);
                setErrors({});
                setVisible(true);
            }}
            tooltip="Edit"
            tooltipOptions={{ position: 'top' }}
        />
    );

    const renderFormField = (key) => {
        const lk = key.toLowerCase();
        const isPlatform = lk.includes('platform');
        const isStatus = lk.includes('status');
        const isDate = lk.includes('date');
        const error = errors[key];
        const value = item[key];

        return (
            <div className="field-group" key={key}>
                <label className="field-label">
                    {key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                    {VALIDATION_RULES[active]?.[key]?.required && <span className="required-mark"> *</span>}
                </label>
                {isStatus ? (
                    <Dropdown value={value} options={statusOptions} onChange={(e) => setItem({ ...item, [key]: e.value })} placeholder="Select status" className={error ? 'p-invalid' : ''} />
                ) : isPlatform && active !== 'platform' ? (
                    <Dropdown value={value} options={platforms} onChange={(e) => setItem({ ...item, [key]: e.value })} placeholder="Select platform" filter className={error ? 'p-invalid' : ''} />
                ) : isDate ? (
                    <Calendar value={value instanceof Date ? value : null} onChange={(e) => setItem({ ...item, [key]: e.value })} dateFormat="dd-mm-yy" placeholder="DD-MM-YYYY" showIcon className={error ? 'p-invalid' : ''} />
                ) : (
                    <InputText value={value || ''} onChange={(e) => setItem({ ...item, [key]: e.target.value })} className={error ? 'p-invalid' : ''} />
                )}
                {error && (
                    <small className="error-message">
                        <i className="pi pi-exclamation-circle"></i> {error}
                    </small>
                )}
            </div>
        );
    };

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="mb-4">
                <h2 className="text-3xl font-bold mb-2" style={{ color: 'var(--primary-color)' }}>
                    Parent Masters
                </h2>
                <p className="text-color-secondary">View and manage parent master records</p>
            </div>

            <Card className="mb-4 bill-reports-filter-card">
                <div className="flex flex-wrap gap-4 align-items-end bill-reports-filter-row">
                    <div className="flex flex-column" style={{ flex: '1 1 240px', minWidth: '240px' }}>
                        <label htmlFor="module" className="mb-2 font-medium text-sm">
                            Module
                        </label>
                        <Dropdown
                            id="module"
                            value={active}
                            options={tables}
                            onChange={(e) => setActive(e.value)}
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Select Module"
                            className="w-full"
                        />
                    </div>
                    <div className="flex gap-2" style={{ flex: '0 0 auto' }}>
                        <Button label="Reset" icon="pi pi-refresh" className="p-button-secondary" onClick={handleResetFilters} />
                    </div>
                </div>
            </Card>

            <Card className="bill-reports-container">
                <div className="flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                    <div className="flex align-items-center gap-2 flex-wrap">
                        <h3 className="text-xl font-semibold m-0">{cur.label} List</h3>
                        <div className="category-status-tabs">
                            <button className={`status-tab-btn active-tab${statusFilter === 'Active' ? ' selected' : ''}`} onClick={() => setStatusFilter(statusFilter === 'Active' ? null : 'Active')}>
                                <i className="pi pi-check-circle" />
                                Active <span className="tab-count">{activeCount}</span>
                            </button>
                            <button className={`status-tab-btn inactive-tab${statusFilter === 'Inactive' ? ' selected' : ''}`} onClick={() => setStatusFilter(statusFilter === 'Inactive' ? null : 'Inactive')}>
                                <i className="pi pi-times-circle" />
                                Inactive <span className="tab-count">{inactiveCount}</span>
                            </button>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2 align-items-center">
                        <span className="p-input-icon-left" style={{ minWidth: '220px' }}>
                            <i className="pi pi-search" />
                            <InputText
                                value={globalFilterValue}
                                onChange={(e) => {
                                    setGlobalFilterValue(e.target.value);
                                    setFilters({ global: { value: e.target.value, matchMode: FilterMatchMode.CONTAINS } });
                                }}
                                placeholder={`Search ${cur.label}...`}
                                className="w-full"
                            />
                        </span>
                        <Button
                            label="Add New"
                            icon="pi pi-plus"
                            className="p-button-primary"
                            onClick={() => {
                                setItem({});
                                setErrors({});
                                setVisible(true);
                            }}
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                        <ProgressSpinner />
                    </div>
                ) : (
                    <DataTable
                        value={filteredData}
                        filters={filters}
                        globalFilterFields={visibleKeys}
                        paginator
                        rows={10}
                        rowsPerPageOptions={[10, 25, 50]}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                        showGridlines
                        tableStyle={{ minWidth: '40rem' }}
                        className="p-datatable-sm"
                        emptyMessage={`No ${statusFilter ? statusFilter.toLowerCase() + ' ' : ''}${cur.label.toLowerCase()} records found`}
                    >
                        {visibleKeys.map((key) => (
                            <Column key={`${active}_${key}`} field={key} header={key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())} sortable body={(rd) => renderCell(key, rd)} />
                        ))}
                        <Column header="Actions" body={actionBodyTemplate} style={{ width: '90px', textAlign: 'center' }} />
                    </DataTable>
                )}
            </Card>

            <Dialog
                visible={visible}
                onHide={() => { setVisible(false); setItem({}); setErrors({}); }}
                header={
                    <div className="dialog-header-custom">
                        <i className={`${cur.icon} header-icon`}></i>
                        <span>{item[cur.pk] ? `Edit ${cur.label}` : `Add ${cur.label}`}</span>
                    </div>
                }
                style={{ width: '500px', maxWidth: '95vw' }}
                modal
                className="category-dialog"
                draggable={false}
                resizable={false}
                footer={
                    <div className="dialog-footer">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-text p-button-secondary cancel-btn"
                            onClick={() => { setVisible(false); setItem({}); setErrors({}); }}
                        />
                        <Button label={item[cur.pk] ? 'Update' : 'Submit'} icon="pi pi-check" className="p-button-raised save-btn" onClick={confirmSave} />
                    </div>
                }
            >
                <div className="dialog-content">{formKeys.map((key) => renderFormField(key))}</div>
            </Dialog>
        </>
    );
};

export default Parentmasters;
